// apps/admin-ui/src/utils/auth.ts
// Authentication utilities for Admin UI
// ALL RIGHTS RESERVED
//
// UPDATED 2026-02-07: Phase 2 RBAC — fixed 3 permission helpers that referenced
//   non-existent permissions (canViewCompliance, canViewTreasury, canViewReserve).
//   Helpers are available for component-level permission checks.

import type { Role, Permission } from './rbac';
import { hasPermission as rbacHasPermission, canAccessApp } from './rbac';

// =============================================================================
// TYPES
// =============================================================================

export interface Employee {
  id: string;
  email: string;
  name: string;
  roles: Role[];
  status?: string;
}

// =============================================================================
// STORAGE KEYS
// =============================================================================

const STORAGE_KEY_EMPLOYEE_ID = 'cxxr_admin_employee_id';
const STORAGE_KEY_FINGERPRINT = 'cxxr_admin_cert_fingerprint';
const STORAGE_KEY_EMPLOYEE_DATA = 'cxxr_admin_employee_data';

// =============================================================================
// STORAGE FUNCTIONS
// =============================================================================

export function getStoredEmployeeId(): string | null {
  return localStorage.getItem(STORAGE_KEY_EMPLOYEE_ID);
}

export function setStoredEmployeeId(employeeId: string): void {
  localStorage.setItem(STORAGE_KEY_EMPLOYEE_ID, employeeId);
}

export function getStoredFingerprint(): string | null {
  return localStorage.getItem(STORAGE_KEY_FINGERPRINT);
}

export function setStoredFingerprint(fingerprint: string): void {
  localStorage.setItem(STORAGE_KEY_FINGERPRINT, fingerprint);
}

export function getStoredEmployeeData(): Employee | null {
  const data = localStorage.getItem(STORAGE_KEY_EMPLOYEE_DATA);
  if (!data) return null;
  try {
    const parsed = JSON.parse(data);
    // Ensure roles is an array
    if (parsed.role && !parsed.roles) {
      parsed.roles = [parsed.role];
    }
    return parsed;
  } catch {
    return null;
  }
}

export function setStoredEmployeeData(employee: Employee): void {
  localStorage.setItem(STORAGE_KEY_EMPLOYEE_DATA, JSON.stringify(employee));
}

export function clearAuthData(): void {
  localStorage.removeItem(STORAGE_KEY_EMPLOYEE_ID);
  localStorage.removeItem(STORAGE_KEY_FINGERPRINT);
  localStorage.removeItem(STORAGE_KEY_EMPLOYEE_DATA);
}

// =============================================================================
// AUTH STATE
// =============================================================================

export function getCurrentEmployee(): Employee | null {
  return getStoredEmployeeData();
}

export function isAuthenticated(): boolean {
  const employee = getStoredEmployeeData();
  if (!employee) return false;
  
  // Check if employee has access to admin-ui
  return canAccessApp(employee.roles, 'admin-ui');
}

export function isDevMode(): boolean {
  return getStoredEmployeeId() !== null && getStoredFingerprint() === null;
}

// =============================================================================
// PERMISSION HELPERS
// Available for component-level permission checks. Layout.tsx and App.tsx use
// hasPermission() directly via RequirePermission / canSee(), but these helpers
// provide a convenient API for page components and hooks.
// =============================================================================

export function canPerform(permission: Permission): boolean {
  const employee = getCurrentEmployee();
  if (!employee) return false;
  return rbacHasPermission(employee.roles, permission);
}

export function canViewDashboard(): boolean {
  return canPerform('admin:view_dashboard');
}

export function canViewOrders(): boolean {
  return canPerform('admin:view_orders');
}

export function canCancelOrder(): boolean {
  return canPerform('admin:cancel_order');
}

export function canRetryOrder(): boolean {
  return canPerform('admin:retry_order');
}

// FIXED: was 'compliance:view_reports' which does not exist in rbac.ts
export function canViewCompliance(): boolean {
  return canPerform('compliance:kyc_aml_review');
}

export function canViewContracts(): boolean {
  return canPerform('contracts:view');
}

// FIXED: was 'treasury:view' which does not exist in rbac.ts
export function canViewTreasury(): boolean {
  return canPerform('reserve:view_balances');
}

// FIXED: was 'reserve:view_reports' which does not exist in rbac.ts
export function canViewReserve(): boolean {
  return canPerform('reserve:view_balances');
}

// =============================================================================
// GOVERNANCE HELPERS
// =============================================================================

export function canAccessGovernance(): boolean {
  return canPerform('governance:view_proposals');
}

export function canVoteOnGovernance(): boolean {
  return canPerform('governance:vote');
}

// =============================================================================
// LOGOUT
// =============================================================================

export function logout(): void {
  clearAuthData();
  window.location.reload();
}
